// src/components/camp/CampPlanPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getCampPlan,
  saveCampTaskCompletion,
  checkAndUpdateCampCompletion,
  getTodayCampCompletion,
  getIndiaDate,
  getFAQs,
} from '../../services/firebase/firestoreService';
import EnhancedCamera from '../exercises/EnhancedCamera';
import PlankCamera from '../exercises/PlankCamera';
import './CampPlanPage.css';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="white" width="20" height="20">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TaskIcon = ({ type, completed }) => {
  const style = completed ? { filter: 'brightness(0) invert(1)' } : {};
  const src = type === 'strength' ? '/icon-strength.svg'
    : type === 'athletics' ? '/icon-athletics.svg'
    : '/icon-nutrition.svg';
  return <img src={src} alt={type} width="22" height="22" style={style} />;
};

const DonutProgress = ({ completed, total }) => {
  const size = 72;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? (completed / total) * circumference : 0;
  const offset = circumference - progress;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none"
        stroke="rgb(255,255,255)" strokeWidth={strokeWidth}/>
      <circle cx={size/2} cy={size/2} r={radius} fill="none"
        stroke="#28a745" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dashoffset 0.4s ease' }}/>
    </svg>
  );
};

const VideoModal = ({ url, onClose }) => {
  const getYouTubeId = (u) => {
    const match = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([^&\n?#/]+)/);
    return match ? match[1] : '';
  };
  return (
    <div className="cp-video-overlay" onClick={onClose}>
      <div className="cp-video-modal" onClick={e => e.stopPropagation()}>
        <button className="cp-video-close" onClick={onClose}>✕</button>
        <div className="cp-video-wrapper">
          <iframe src={`https://www.youtube.com/embed/${getYouTubeId(url)}`}
            title="Exercise Video" frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen/>
        </div>
      </div>
    </div>
  );
};

const FALLBACK_FAQS = {
  en: [
    { q: 'Camera is not opening. What should I do?', a: 'Contact your admin on WhatsApp and they will help you fix it.' },
    { q: 'I completed my tasks but it did not register. What happened?', a: 'Close the app and open it again. If the problem continues, contact your admin.' },
    { q: 'What happens after I complete a camp?', a: 'Your camp will be marked as attended and the next camp will unlock when your admin assigns it.' }
  ],
  mr: [
    { q: 'कॅमेरा उघडत नाही. मी काय करू?', a: 'तुमच्या admin ला WhatsApp वर संपर्क करा, ते तुम्हाला मदत करतील.' },
    { q: 'मी कामे पूर्ण केली पण नोंद झाली नाही. काय झाले?', a: 'अ‍ॅप बंद करा आणि पुन्हा उघडा. समस्या कायम राहिल्यास admin शी संपर्क साधा.' },
    { q: 'कॅम्प पूर्ण केल्यावर काय होते?', a: 'तुमचा कॅम्प attended म्हणून नोंदला जाईल आणि admin पुढचा कॅम्प unlock करेल.' }
  ]
};

const FAQModal = ({ language, faqs, onClose }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="cp-faq-overlay" onClick={onClose}>
      <div className="cp-faq-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-faq-header">
          <span className="cp-faq-title">{language === 'mr' ? 'सामान्य प्रश्न' : 'FAQs'}</span>
          <button className="cp-faq-close" onClick={onClose}>✕</button>
        </div>
        <div className="cp-faq-list">
          {faqs.map((faq, i) => (
            <div key={i} className="cp-faq-item">
              <button
                className={`cp-faq-question ${openIndex === i ? 'open' : ''}`}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span>{faq.q}</span>
                <span className="cp-faq-arrow">{openIndex === i ? '▲' : '▼'}</span>
              </button>
              {openIndex === i && <div className="cp-faq-answer">{faq.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CampPlanPage = ({ campNumber, onBack }) => {
  const { currentUser, userData, refreshUserData } = useAuth();
  const [language, setLanguage] = useState(
    () => localStorage.getItem('spocademy_language') || 'mr'
  );
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [showWhatsAppSupport, setShowWhatsAppSupport] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [faqList, setFaqList] = useState(FALLBACK_FAQS[language] || FALLBACK_FAQS.mr);
  const [videoUrl, setVideoUrl] = useState(null);
  const [cameraFallbackEnabled, setCameraFallbackEnabled] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(null);
  const audioContextRef = useRef(null);
  const celebrationHandledRef = useRef(false);

  const translations = {
    en: {
      camp: 'Camp', markComplete: 'Mark Complete', startCamera: 'Start Camera',
      completed: '✓ Completed', loadingTasks: 'Loading camp plan...',
      noTasksFound: 'No tasks planned for this camp', contactAdmin: 'Contact admin to add tasks',
      whatsappSupport: 'Contact Support', greatWork: 'Great Work!',
      campComplete: 'Camp completed! Amazing effort today!',
      tomorrow: 'See you at the next camp!', continueText: 'Continue', back: '← Back',
      video: 'video', faqs: 'FAQs'
    },
    mr: {
      camp: 'कॅम्प', markComplete: 'पूर्ण झाले', startCamera: 'कॅमेरा सुरू करा',
      completed: '✓ पूर्ण झाले', loadingTasks: 'कॅम्प योजना लोड होत आहे...',
      noTasksFound: 'या कॅम्पसाठी कोणती कामे नाहीत', contactAdmin: 'कामे जोडण्यासाठी admin शी संपर्क साधा',
      whatsappSupport: 'सहाय्यता संपर्क', greatWork: 'अभिनंदन!',
      campComplete: 'कॅम्प पूर्ण! आजचे प्रयत्न अप्रतिम होते!',
      tomorrow: 'पुढच्या कॅम्पमध्ये भेटू!', continueText: 'पुढे जा', back: '← मागे',
      video: 'video', faqs: 'सामान्य प्रश्न'
    }
  };

  const t = translations[language];

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
    setFaqList(FALLBACK_FAQS[newLang]);
  };

  const india = getIndiaDate();
  const days = language === 'en'
    ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    : ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'];
  const months = language === 'en'
    ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    : ['जाने', 'फेब्रु', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑग', 'सप्टें', 'ऑक्टो', 'नोव्हें', 'डिसें'];
  const dateString = `${days[india.getDay()]}, ${months[india.getMonth()]} ${india.getDate()}`;
const firstName = userData?.firstName || '';

  useEffect(() => {
    if (!currentUser) return;
    const loadData = async () => {
      try {
        setLoadingTasks(true);
        setCameraFallbackEnabled(userData?.cameraFallbackEnabled || false);
        const [campPlan, todayCompletion] = await Promise.all([
          getCampPlan(campNumber),
          getTodayCampCompletion(currentUser.uid, campNumber)
        ]);
        if (!campPlan || !campPlan.tasks || campPlan.tasks.length === 0) {
          setShowWhatsAppSupport(true);
          setTasks([]);
        } else {
          setTasks(campPlan.tasks);
          if (todayCompletion?.completedTasks) {
            setCompletedTasks(new Set(Object.keys(todayCompletion.completedTasks)));
          }
        }
      } catch (e) {
        setShowWhatsAppSupport(true);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadData();
  }, [campNumber, currentUser, userData?.cameraFallbackEnabled]);

  useEffect(() => {
    if (
      tasks.length > 0 &&
      completedTasks.size === tasks.length &&
      !celebrationHandledRef.current &&
      currentUser
    ) {
      celebrationHandledRef.current = true;
      const handleCompletion = async () => {
        try {
          const completed = await checkAndUpdateCampCompletion(
            currentUser.uid, campNumber, tasks.length
          );
          if (completed) {
            if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
            setTimeout(() => setShowCelebration(true), 600);
          }
        } catch (e) {}
      };
      handleCompletion();
    }
  }, [completedTasks.size, tasks.length, currentUser, campNumber]);

  const initAudio = () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {}
    }
  };

  const triggerHaptic = (type) => {
    if (!navigator.vibrate) return;
    if (type === 'light') navigator.vibrate(50);
  };

  const playSuccessSound = () => {
    initAudio();
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(523, ctx.currentTime);
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  };

  const playAudio = (task) => {
    initAudio();
    if (!('speechSynthesis' in window)) return;
    const taskName = typeof task.name === 'object'
      ? (task.name[language] || task.name.en) : task.name;
    let text = taskName;
    if (task.sets && task.reps) {
      text += language === 'mr'
        ? `. ${task.sets} सेट, ${task.reps} रेप्स`
        : `. ${task.sets} sets of ${task.reps} reps`;
      if (task.restTime) {
        text += language === 'mr'
          ? `, ${task.restTime} सेकंद विश्रांती`
          : `, ${task.restTime} seconds rest`;
      }
    }
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === 'mr' ? 'hi-IN' : 'en-US';
    utterance.rate = 0.85;
    speechSynthesis.speak(utterance);
  };

  const handleTaskComplete = async (taskId) => {
    if (completedTasks.has(taskId) || !currentUser) return;
    initAudio();
    triggerHaptic('light');
    playSuccessSound();
    try {
      await saveCampTaskCompletion(currentUser.uid, campNumber, taskId);
      setCompletedTasks(prev => new Set([...prev, taskId]));
    } catch (e) {}
  };

  const handleFAQOpen = async () => {
    try {
      const all = await getFAQs();
      const active = all
        .filter(f => f.active === true)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (active.length > 0) {
        setFaqList(active.map(f => ({
          q: language === 'mr' ? f.questionMr : f.questionEn,
          a: language === 'mr' ? f.answerMr : f.answerEn
        })));
      }
    } catch (e) {
      // keep fallback
    }
    setShowFAQ(true);
  };

  const handleCelebrationClose = async () => {
    setShowCelebration(false);
    try {
      if (refreshUserData) await refreshUserData();
    } catch (e) {}
    if (onBack) onBack();
  };

  const handleWhatsApp = () => {
    const message = language === 'mr'
      ? `नमस्कार, कॅम्प ${campNumber} साठी कामे जोडलेली नाहीत.`
      : `Hi, no tasks found for Camp ${campNumber}.`;
    window.open(`https://wa.me/919359246193?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getTaskDescription = (task) => {
    if (!task.sets && !task.reps) return null;
    const parts = [];
    if (task.sets && task.reps) {
      parts.push(language === 'mr'
        ? `${task.sets} सेट × ${task.reps} रेप्स`
        : `${task.sets} sets × ${task.reps} reps`);
    }
    if (task.restTime) {
      parts.push(language === 'mr'
        ? `${task.restTime} सेकंद विश्रांती`
        : `${task.restTime}s rest`);
    }
    return parts.join(' · ');
  };

  const renderTaskCard = (task) => {
    const isCompleted = completedTasks.has(task.id);
    const taskName = typeof task.name === 'object'
      ? (task.name[language] || task.name.en) : task.name;
    const description = getTaskDescription(task);
    const useCamera = task.hasMediaPipe && !cameraFallbackEnabled;

    return (
      <div key={task.id} className={`cp-task-card ${isCompleted ? 'completed' : ''}`}>
        {task.youtubeURL && (
          <button className="cp-video-btn" onClick={() => setVideoUrl(task.youtubeURL)}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="rgba(220,0,0,0.6)">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
            </svg>
            <span>{t.video}</span>
          </button>
        )}
        <div className="cp-task-header">
          <div className="cp-task-icon-wrap">
            <TaskIcon type={task.type} completed={isCompleted} />
          </div>
          <div className="cp-task-info">
            <div className="cp-task-name">{taskName}</div>
            {description && <div className="cp-task-description">{description}</div>}
            {task.comment && <div className="cp-task-comment">{task.comment}</div>}
          </div>
        </div>
        <div className="cp-task-controls">
          <button className="cp-audio-btn" onClick={() => playAudio(task)}>🔊</button>
          {isCompleted ? (
            <div className="cp-completed-indicator">{t.completed}</div>
          ) : (
            <button className="cp-start-btn" onClick={() => {
              if (useCamera) {
                setCurrentExercise(task);
              } else {
                handleTaskComplete(task.id);
              }
            }}>
              {useCamera ? t.startCamera : t.markComplete}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="cp-container">
      <div className="cp-header">
        <div className="cp-header-top">
          <button className="cp-back-btn" onClick={onBack}>{t.back}</button>
          <button className="cp-lang-toggle" onClick={handleLanguageToggle}>
            {language === 'en' ? 'मराठी' : 'EN'}
          </button>
        </div>
        <div className="cp-header-center">
          <DonutProgress completed={completedTasks.size} total={tasks.length} />
          <span className="cp-progress-text">{completedTasks.size}/{tasks.length}</span>
        </div>
        <div className="cp-header-bottom">
          <div className="cp-left">
            <div className="cp-user-name">{firstName}</div>
            <div className="cp-date">{dateString}</div>
          </div>
          <div className="cp-camp-badge">{t.camp} {campNumber}</div>
        </div>
      </div>

      <div className="cp-task-list">
        {loadingTasks ? (
          <div className="cp-state-message">
            <div className="cp-spinner" />
            <p>{t.loadingTasks}</p>
          </div>
        ) : showWhatsAppSupport ? (
          <div className="cp-state-message">
            <div className="cp-state-icon">📋</div>
            <p className="cp-state-title">{t.noTasksFound}</p>
            <p className="cp-state-sub">{t.contactAdmin}</p>
            <button className="cp-whatsapp-support-btn" onClick={handleWhatsApp}>
              <WhatsAppIcon />
              {t.whatsappSupport}
            </button>
          </div>
        ) : (
          <>
            {tasks.map(task => renderTaskCard(task))}
            <button className="cp-faq-trigger-btn" onClick={handleFAQOpen}>
              <div className="cp-faq-trigger-icon">?</div>
            </button>
          </>
        )}
      </div>

      {videoUrl && <VideoModal url={videoUrl} onClose={() => setVideoUrl(null)} />}
      {showFAQ && <FAQModal language={language} faqs={faqList} onClose={() => setShowFAQ(false)} />}

      {currentExercise && currentExercise.mediaPipeType === 'plank' && (
        <PlankCamera
          exercise={currentExercise}
          userId={currentUser ? currentUser.uid : ''}
          onComplete={() => {
            handleTaskComplete(currentExercise.id);
            setCurrentExercise(null);
          }}
          onBack={() => setCurrentExercise(null)}
        />
      )}
      {currentExercise && currentExercise.mediaPipeType !== 'plank' && (
        <EnhancedCamera
          exercise={currentExercise}
          userId={currentUser ? currentUser.uid : ''}
          onComplete={() => {
            handleTaskComplete(currentExercise.id);
            setCurrentExercise(null);
          }}
          onBack={() => setCurrentExercise(null)}
        />
      )}

      {showCelebration && (
        <div className="cp-celebration-overlay">
          <div className="cp-celebration-content">
            <div className="cp-celebration-icon">🎉</div>
            <div className="cp-celebration-title">{t.greatWork}</div>
            <div className="cp-celebration-message">{t.campComplete}</div>
            <div className="cp-celebration-sub">{t.tomorrow}</div>
            <button className="cp-celebration-btn" onClick={handleCelebrationClose}>
              {t.continueText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampPlanPage;